import React from 'react';
import { FileSpreadsheet, Zap, Shield, CheckCircle, Camera, Download, Brain, Clock, ChevronDown } from 'lucide-react';
import { Button } from '../components/ui/button';

const REDIRECT_URL = `${window.location.origin}/app`;
const AUTH_URL = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(REDIRECT_URL)}`;

const LandingPage = () => {
  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
      {/* Header */}
      <header className="bg-white/90 backdrop-blur-lg border-b border-emerald-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="bg-gradient-to-br from-emerald-600 via-teal-600 to-cyan-600 p-2.5 rounded-xl shadow-lg">
                <FileSpreadsheet className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold bg-gradient-to-r from-emerald-700 to-teal-700 bg-clip-text text-transparent">
                  Payslip<span className="font-normal">2</span>Excel
                </h1>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Button
                onClick={() => window.location.href = AUTH_URL}
                variant="outline"
                className="hidden sm:flex border-2 border-emerald-600 text-emerald-700 hover:bg-emerald-50"
              >
                Sign In
              </Button>
              <Button
                onClick={() => window.location.href = AUTH_URL}
                className="bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 hover:from-emerald-700 hover:via-teal-700 hover:to-cyan-700 text-white shadow-lg hover:shadow-xl transition-all duration-300"
              >
                Get Started Free
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden py-20 lg:py-32">
        {/* Animated background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-10 w-72 h-72 bg-emerald-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob"></div>
          <div className="absolute top-40 right-10 w-72 h-72 bg-teal-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob animation-delay-2000"></div>
          <div className="absolute bottom-20 left-1/2 w-72 h-72 bg-cyan-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob animation-delay-4000"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-100 rounded-full border border-emerald-300 mb-8 animate-fadeIn">
              <Zap className="w-4 h-4 text-emerald-600" />
              <span className="text-sm font-semibold text-emerald-800">AI-Powered • 10 Free Conversions</span>
            </div>
            
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-slate-900 mb-6 leading-tight animate-fadeIn">
              Convert Financial Documents to{' '}
              <span className="bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
                Excel
              </span>{' '}
              in Seconds
            </h1>
            
            <p className="text-xl md:text-2xl text-slate-600 mb-10 leading-relaxed animate-fadeIn">
              Transform payslips, bank statements, and utility bills into structured Excel files with our AI-powered converter. 
              <span className="font-semibold text-emerald-700"> No credit card required.</span>
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center animate-fadeIn">
              <Button
                onClick={() => window.location.href = AUTH_URL}
                size="lg"
                className="bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 hover:from-emerald-700 hover:via-teal-700 hover:to-cyan-700 text-white px-8 py-6 text-lg rounded-xl shadow-2xl hover:shadow-3xl transition-all duration-300 transform hover:-translate-y-1 hover:scale-105"
              >
                <FileSpreadsheet className="w-6 h-6 mr-2" />
                Start Converting Free
              </Button>
              <Button
                onClick={() => scrollToSection('features')}
                size="lg"
                variant="outline"
                className="border-2 border-slate-300 text-slate-700 hover:bg-slate-50 px-8 py-6 text-lg rounded-xl"
              >
                See How It Works
                <ChevronDown className="w-5 h-5 ml-2" />
              </Button>
            </div>

            <div className="mt-12 flex items-center justify-center gap-8 text-sm text-slate-600">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-emerald-600" />
                <span>10 Free Conversions</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-emerald-600" />
                <span>No Credit Card</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-emerald-600" />
                <span>GDPR Compliant</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Powerful Features for Your Financial Data
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Everything you need to convert and analyze your financial documents efficiently
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div className="p-8 rounded-2xl bg-gradient-to-br from-purple-50 to-pink-50 border-2 border-purple-200 hover:border-purple-400 transition-all duration-300 hover:shadow-xl transform hover:-translate-y-2">
              <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center mb-6 shadow-lg">
                <Camera className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-3">Photo & PDF Support</h3>
              <p className="text-slate-600 leading-relaxed">
                Upload PDFs or simply take a photo of your documents. Our OCR technology extracts data from any format.
              </p>
            </div>

            {/* Feature 2 */}
            <div className="p-8 rounded-2xl bg-gradient-to-br from-emerald-50 to-teal-50 border-2 border-emerald-200 hover:border-emerald-400 transition-all duration-300 hover:shadow-xl transform hover:-translate-y-2">
              <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-xl flex items-center justify-center mb-6 shadow-lg">
                <Brain className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-3">AI-Powered Parsing</h3>
              <p className="text-slate-600 leading-relaxed">
                Advanced AI automatically detects document types and extracts data with 99% accuracy using smart pattern recognition.
              </p>
            </div>

            {/* Feature 3 */}
            <div className="p-8 rounded-2xl bg-gradient-to-br from-cyan-50 to-blue-50 border-2 border-cyan-200 hover:border-cyan-400 transition-all duration-300 hover:shadow-xl transform hover:-translate-y-2">
              <div className="w-14 h-14 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-xl flex items-center justify-center mb-6 shadow-lg">
                <FileSpreadsheet className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-3">Professional Excel Output</h3>
              <p className="text-slate-600 leading-relaxed">
                Get beautifully formatted Excel files with multiple sheets, summaries, and categorized data ready for analysis.
              </p>
            </div>

            {/* Feature 4 */}
            <div className="p-8 rounded-2xl bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-200 hover:border-amber-400 transition-all duration-300 hover:shadow-xl transform hover:-translate-y-2">
              <div className="w-14 h-14 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl flex items-center justify-center mb-6 shadow-lg">
                <Zap className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-3">Lightning Fast</h3>
              <p className="text-slate-600 leading-relaxed">
                Process multiple documents in seconds. Batch convert up to 50 files at once for maximum productivity.
              </p>
            </div>

            {/* Feature 5 */}
            <div className="p-8 rounded-2xl bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200 hover:border-green-400 transition-all duration-300 hover:shadow-xl transform hover:-translate-y-2">
              <div className="w-14 h-14 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl flex items-center justify-center mb-6 shadow-lg">
                <Shield className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-3">100% Private & Secure</h3>
              <p className="text-slate-600 leading-relaxed">
                Your data is processed securely and never stored. Files are auto-deleted within 24 hours. GDPR compliant.
              </p>
            </div>

            {/* Feature 6 */}
            <div className="p-8 rounded-2xl bg-gradient-to-br from-rose-50 to-red-50 border-2 border-rose-200 hover:border-rose-400 transition-all duration-300 hover:shadow-xl transform hover:-translate-y-2">
              <div className="w-14 h-14 bg-gradient-to-br from-rose-500 to-red-500 rounded-xl flex items-center justify-center mb-6 shadow-lg">
                <Download className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-3">Instant Download</h3>
              <p className="text-slate-600 leading-relaxed">
                Preview your data before downloading. Edit any fields if needed, then download your formatted Excel file instantly.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="benefits" className="py-20 bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Why Choose Payslip2Excel?
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Save time, reduce errors, and streamline your financial document processing
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center">
                    <Clock className="w-6 h-6 text-emerald-600" />
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">Save Hours of Manual Data Entry</h3>
                  <p className="text-slate-600">
                    Eliminate tedious copy-pasting. Convert an entire month's bank statements in under a minute.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                    <CheckCircle className="w-6 h-6 text-purple-600" />
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">Eliminate Human Errors</h3>
                  <p className="text-slate-600">
                    AI-powered validation ensures 99%+ accuracy. No more typos or missed transactions.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-cyan-100 rounded-lg flex items-center justify-center">
                    <Brain className="w-6 h-6 text-cyan-600" />
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">Smart Categorization</h3>
                  <p className="text-slate-600">
                    Automatically categorize transactions by type (groceries, dining, transport, etc.) for instant insights.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                    <FileSpreadsheet className="w-6 h-6 text-amber-600" />
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">Ready for Analysis</h3>
                  <p className="text-slate-600">
                    Export to Excel with pivot tables, charts, and summaries. Perfect for accounting software integration.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-3xl shadow-2xl p-8 border-2 border-emerald-200">
              <div className="text-center mb-8">
                <h3 className="text-3xl font-bold text-slate-900 mb-2">Free Tier</h3>
                <p className="text-slate-600">Perfect for individuals</p>
              </div>
              <div className="space-y-4 mb-8">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-6 h-6 text-emerald-600 flex-shrink-0" />
                  <span className="text-slate-700">10 free conversions per account</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-6 h-6 text-emerald-600 flex-shrink-0" />
                  <span className="text-slate-700">All document types supported</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-6 h-6 text-emerald-600 flex-shrink-0" />
                  <span className="text-slate-700">Batch upload (up to 50 files)</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-6 h-6 text-emerald-600 flex-shrink-0" />
                  <span className="text-slate-700">PDF & photo support</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-6 h-6 text-emerald-600 flex-shrink-0" />
                  <span className="text-slate-700">AI-powered accuracy</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-6 h-6 text-emerald-600 flex-shrink-0" />
                  <span className="text-slate-700">No credit card required</span>
                </div>
              </div>
              <Button
                onClick={() => window.location.href = AUTH_URL}
                className="w-full bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 hover:from-emerald-700 hover:via-teal-700 hover:to-cyan-700 text-white py-6 text-lg rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
              >
                Get Started Free
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-slate-600">
              Everything you need to know about Payslip2Excel
            </p>
          </div>

          <div className="space-y-6">
            <details className="group bg-slate-50 rounded-xl p-6 cursor-pointer hover:bg-slate-100 transition-all duration-200">
              <summary className="text-xl font-bold text-slate-900 flex items-center justify-between">
                What documents can I convert?
                <ChevronDown className="w-5 h-5 group-open:rotate-180 transition-transform" />
              </summary>
              <p className="mt-4 text-slate-600 leading-relaxed">
                We support payslips, bank statements, and utility bills. You can upload PDFs or take photos of physical documents. 
                We automatically detect the document type and extract all relevant data.
              </p>
            </details>

            <details className="group bg-slate-50 rounded-xl p-6 cursor-pointer hover:bg-slate-100 transition-all duration-200">
              <summary className="text-xl font-bold text-slate-900 flex items-center justify-between">
                Is my data secure?
                <ChevronDown className="w-5 h-5 group-open:rotate-180 transition-transform" />
              </summary>
              <p className="mt-4 text-slate-600 leading-relaxed">
                Absolutely! Your documents are processed securely on our servers and are NEVER stored permanently. 
                All files are automatically deleted within 24 hours. We're fully GDPR compliant and take your privacy seriously.
              </p>
            </details>

            <details className="group bg-slate-50 rounded-xl p-6 cursor-pointer hover:bg-slate-100 transition-all duration-200">
              <summary className="text-xl font-bold text-slate-900 flex items-center justify-between">
                How accurate is the AI conversion?
                <ChevronDown className="w-5 h-5 group-open:rotate-180 transition-transform" />
              </summary>
              <p className="mt-4 text-slate-600 leading-relaxed">
                Our AI achieves 99%+ accuracy on clear documents. We use advanced OCR and machine learning to extract data. 
                Plus, you can preview and edit any fields before downloading, ensuring 100% accuracy.
              </p>
            </details>

            <details className="group bg-slate-50 rounded-xl p-6 cursor-pointer hover:bg-slate-100 transition-all duration-200">
              <summary className="text-xl font-bold text-slate-900 flex items-center justify-between">
                What happens after 10 free conversions?
                <ChevronDown className="w-5 h-5 group-open:rotate-180 transition-transform" />
              </summary>
              <p className="mt-4 text-slate-600 leading-relaxed">
                After your 10 free conversions, you'll need to upgrade to a paid plan to continue. 
                We'll notify you when you're approaching your limit so there are no surprises.
              </p>
            </details>

            <details className="group bg-slate-50 rounded-xl p-6 cursor-pointer hover:bg-slate-100 transition-all duration-200">
              <summary className="text-xl font-bold text-slate-900 flex items-center justify-between">
                Which banks do you support?
                <ChevronDown className="w-5 h-5 group-open:rotate-180 transition-transform" />
              </summary>
              <p className="mt-4 text-slate-600 leading-relaxed">
                We support all major UK banks including Barclays, HSBC, Lloyds, NatWest, TSB, Santander, Halifax, Nationwide, and Monzo. 
                Our AI automatically detects the bank and uses bank-specific parsing for maximum accuracy.
              </p>
            </details>

            <details className="group bg-slate-50 rounded-xl p-6 cursor-pointer hover:bg-slate-100 transition-all duration-200">
              <summary className="text-xl font-bold text-slate-900 flex items-center justify-between">
                Can I use photos instead of PDFs?
                <ChevronDown className="w-5 h-5 group-open:rotate-180 transition-transform" />
              </summary>
              <p className="mt-4 text-slate-600 leading-relaxed">
                Yes! Simply take a clear photo of your document with your phone or camera. Our OCR technology can extract data from photos 
                in JPG, PNG, and other common formats. Just make sure the photo is well-lit and in focus for best results.
              </p>
            </details>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-white/10"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Streamline Your Financial Data?
          </h2>
          <p className="text-xl text-emerald-50 mb-10">
            Join thousands of users who save hours every month with Payslip2Excel
          </p>
          <Button
            onClick={() => window.location.href = AUTH_URL}
            size="lg"
            className="bg-white text-emerald-700 hover:bg-emerald-50 px-10 py-6 text-lg rounded-xl shadow-2xl hover:shadow-3xl transition-all duration-300 transform hover:-translate-y-1 hover:scale-105 font-bold"
          >
            <FileSpreadsheet className="w-6 h-6 mr-2" />
            Start Your Free Trial Now
          </Button>
          <p className="mt-6 text-emerald-100 text-sm">
            No credit card required • 10 free conversions • Cancel anytime
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center space-x-3 mb-4 md:mb-0">
              <div className="bg-gradient-to-br from-emerald-600 via-teal-600 to-cyan-600 p-2 rounded-lg">
                <FileSpreadsheet className="w-6 h-6 text-white" />
              </div>
              <div>
                <span className="text-xl font-bold">Payslip2Excel</span>
              </div>
            </div>
            <div className="text-slate-400 text-sm">
              &copy; 2025 Payslip2Excel. Privacy-first document processing. GDPR compliant.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
